import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Gráfico de linhas para séries temporais (tráfego, latência, erros).
 *
 * É desenhado em SVG à mão, sem biblioteca de gráficos, por dois motivos:
 * o app não tem nenhuma dependência de UI hoje (e uma biblioteca de charts
 * costuma ser maior que o resto do bundle), e o painel precisa de poucos
 * elementos — linha, grade, marcador e tooltip.
 *
 * Regras visuais seguidas aqui (as mesmas em todo o painel):
 *   • linha de 2px, marcador do último ponto com anel na cor do fundo;
 *   • grade fina e recessiva — o dado é a única coisa que pode gritar;
 *   • legenda SEMPRE que houver 2+ séries, e rótulo direto no fim da linha
 *     quando ele couber sem colidir com o vizinho;
 *   • tabela equivalente embutida, para quem não distingue as cores e para
 *     quem precisa do número exato.
 */

/** Uma série do gráfico. */
export interface ChartSeries {
  key: string;
  label: string;
  /** Cor da MARCA (linha/ponto). Texto nunca usa a cor da série. */
  color: string;
  values: number[];
}

interface TimeSeriesChartProps {
  title: string;
  description?: string;
  /** Instantes (ISO) de cada ponto — precisam ter o mesmo tamanho das séries. */
  labels: string[];
  series: ChartSeries[];
  /** Formatação do valor no eixo, no rótulo e no tooltip. */
  formatValue?: (value: number) => string;
  height?: number;
  /** Preenche a área sob a linha (só faz sentido com uma série). */
  filled?: boolean;
}

const PADDING = { top: 12, right: 56, bottom: 22, left: 46 };
const HORIZONTAL_GRID_LINES = 4;
/** Distância mínima, em px, entre dois rótulos diretos no fim das linhas. */
const MIN_LABEL_GAP = 15;
const DEFAULT_HEIGHT = 220;

export function TimeSeriesChart({
  title,
  description,
  labels,
  series,
  formatValue = (value) => value.toLocaleString('pt-BR'),
  height = DEFAULT_HEIGHT,
  filled = false,
}: TimeSeriesChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const pointCount = labels.length;
  const plotWidth = Math.max(0, width - PADDING.left - PADDING.right);
  const plotHeight = height - PADDING.top - PADDING.bottom;

  // Contagens (requisições, erros, milissegundos) são sempre inteiras, e um
  // eixo com "0,25 requisição" seria absurdo — daí a checagem.
  const hasOnlyIntegers = series.every((item) => item.values.every(Number.isInteger));
  const maxValue = niceAxisMax(
    Math.max(0, ...series.flatMap((item) => item.values)),
    HORIZONTAL_GRID_LINES,
    hasOnlyIntegers,
  );
  const xAt = (index: number) =>
    PADDING.left + (pointCount <= 1 ? plotWidth / 2 : (index / (pointCount - 1)) * plotWidth);
  const yAt = (value: number) => PADDING.top + plotHeight - (value / maxValue) * plotHeight;

  const handlePointer = useCallback(
    (event: React.PointerEvent<SVGSVGElement>) => {
      if (pointCount === 0 || plotWidth <= 0) return;

      const bounds = event.currentTarget.getBoundingClientRect();
      const position = event.clientX - bounds.left - PADDING.left;
      const ratio = Math.min(1, Math.max(0, position / plotWidth));

      setHoverIndex(Math.round(ratio * (pointCount - 1)));
    },
    [pointCount, plotWidth],
  );

  // Sem largura medida ainda (primeira renderização): reserva o espaço para o
  // gráfico não "pular" quando aparecer.
  if (width === 0) {
    return (
      <figure className="chart" ref={containerRef}>
        <figcaption className="chart__title">{title}</figcaption>
        <div className="chart__canvas" style={{ height }} />
      </figure>
    );
  }

  const activeIndex = hoverIndex ?? pointCount - 1;
  const directLabels = resolveDirectLabels(series, yAt);

  return (
    <figure className="chart" ref={containerRef}>
      <figcaption className="chart__header">
        <span className="chart__title">{title}</span>
        {description && <span className="chart__description">{description}</span>}
      </figcaption>

      {series.length > 1 && (
        <ul className="chart__legend">
          {series.map((item) => (
            <li className="chart__legend-item" key={item.key}>
              <span className="chart__legend-dot" style={{ backgroundColor: item.color }} />
              {item.label}
            </li>
          ))}
        </ul>
      )}

      <div className="chart__canvas">
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={`${title}. ${series
            .map((item) => `${item.label}: ${formatValue(item.values.at(-1) ?? 0)}`)
            .join('; ')}`}
          onPointerMove={handlePointer}
          onPointerLeave={() => setHoverIndex(null)}
        >
          {/* Grade e eixo Y */}
          {Array.from({ length: HORIZONTAL_GRID_LINES + 1 }, (_, step) => {
            const value = (maxValue / HORIZONTAL_GRID_LINES) * step;
            const y = yAt(value);
            return (
              <g key={step}>
                <line
                  x1={PADDING.left}
                  x2={width - PADDING.right}
                  y1={y}
                  y2={y}
                  className="chart__gridline"
                />
                <text x={PADDING.left - 8} y={y + 4} className="chart__axis-label" textAnchor="end">
                  {formatValue(value)}
                </text>
              </g>
            );
          })}

          {/* Eixo do tempo: primeiro, meio e último instante */}
          {[0, Math.floor((pointCount - 1) / 2), pointCount - 1].map((index, position) => (
            <text
              key={`${index}-${position}`}
              x={xAt(index)}
              y={height - 6}
              className="chart__axis-label"
              textAnchor={position === 0 ? 'start' : position === 1 ? 'middle' : 'end'}
            >
              {formatTimeTick(labels[index], labels)}
            </text>
          ))}

          {/* Marca do ponteiro */}
          {hoverIndex !== null && (
            <line
              x1={xAt(hoverIndex)}
              x2={xAt(hoverIndex)}
              y1={PADDING.top}
              y2={PADDING.top + plotHeight}
              className="chart__crosshair"
            />
          )}

          {series.map((item) => {
            const linePath = buildLinePath(item.values, xAt, yAt);
            return (
              <g key={item.key}>
                {filled && (
                  <path
                    d={`${linePath} L ${xAt(pointCount - 1)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`}
                    fill={item.color}
                    fillOpacity={0.1}
                  />
                )}
                <path d={linePath} fill="none" stroke={item.color} className="chart__line" />
                {/* Anel na cor da superfície: mantém o ponto legível quando
                    duas séries se cruzam exatamente ali. */}
                <circle
                  cx={xAt(pointCount - 1)}
                  cy={yAt(item.values.at(-1) ?? 0)}
                  r={4}
                  fill={item.color}
                  className="chart__end-dot"
                />
                {hoverIndex !== null && (
                  <circle
                    cx={xAt(hoverIndex)}
                    cy={yAt(item.values[hoverIndex] ?? 0)}
                    r={4}
                    fill={item.color}
                    className="chart__end-dot"
                  />
                )}
              </g>
            );
          })}

          {/* Rótulo direto no fim da linha — só nas séries que não colidem */}
          {series.map((item, index) =>
            directLabels[index] ? (
              <text
                key={`label-${item.key}`}
                x={width - PADDING.right + 8}
                y={yAt(item.values.at(-1) ?? 0) + 4}
                className="chart__series-label"
              >
                {formatValue(item.values.at(-1) ?? 0)}
              </text>
            ) : null,
          )}
        </svg>

        {hoverIndex !== null && (
          <div
            className="chart__tooltip"
            style={{
              left: `${Math.min(Math.max(xAt(hoverIndex), 70), width - 70)}px`,
            }}
          >
            <p className="chart__tooltip-time">{formatFullTime(labels[activeIndex])}</p>
            {series.map((item) => (
              <p className="chart__tooltip-row" key={item.key}>
                <span className="chart__legend-dot" style={{ backgroundColor: item.color }} />
                {item.label}: <strong>{formatValue(item.values[activeIndex] ?? 0)}</strong>
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Equivalente textual: garante o número exato a quem não lê o gráfico */}
      <details className="chart__table">
        <summary>Ver dados em tabela</summary>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Instante</th>
                {series.map((item) => (
                  <th scope="col" key={item.key}>
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((label, index) => (
                <tr key={label}>
                  <td>{formatFullTime(label)}</td>
                  {series.map((item) => (
                    <td className="data-table__number" key={item.key}>
                      {formatValue(item.values[index] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/**
 * Largura real do container, em pixels.
 *
 * Medir em vez de usar `viewBox` elástico é o que mantém a linha com 2px de
 * verdade e o texto do eixo legível no celular — com viewBox esticado, os dois
 * seriam distorcidos pela proporção da tela.
 */
function useElementWidth(ref: React.RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);

  return width;
}

/** Caminho SVG da linha de uma série. */
function buildLinePath(
  values: number[],
  xAt: (index: number) => number,
  yAt: (value: number) => number,
): string {
  return values
    .map((value, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index)} ${yAt(value)}`)
    .join(' ');
}

/**
 * Topo do eixo Y escolhido a partir de um PASSO redondo.
 *
 * Arredondar só o topo não basta: um topo de 5 dividido em 4 linhas produz
 * marcas em 1,25 / 2,5 / 3,75 — números que ninguém compara de cabeça. Aqui o
 * passo é que é redondo (1, 2, 2,5, 5 × 10ⁿ), e o topo sai dele.
 *
 * @param rawMax Maior valor das séries.
 * @param gridLines Quantas divisões o eixo terá.
 * @param integersOnly Força passo inteiro quando os dados são contagens.
 */
function niceAxisMax(rawMax: number, gridLines: number, integersOnly: boolean): number {
  if (rawMax <= 0) return gridLines;

  const roughStep = rawMax / gridLines;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const multiplier =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;

  const step = multiplier * magnitude;

  return (integersOnly ? Math.max(step, 1) : step) * gridLines;
}

/**
 * Decide quais séries podem exibir rótulo direto.
 *
 * Quando duas linhas terminam quase no mesmo ponto, empilhar os rótulos os
 * desgruda das linhas e vira ruído: nesse caso o rótulo é omitido e a legenda
 * (mais o tooltip e a tabela) carrega a identidade.
 */
function resolveDirectLabels(series: ChartSeries[], yAt: (value: number) => number): boolean[] {
  const positions = series.map((item, index) => ({ index, y: yAt(item.values.at(-1) ?? 0) }));
  const visible = new Array<boolean>(series.length).fill(true);

  positions
    .slice()
    .sort((first, second) => first.y - second.y)
    .forEach((current, order, sorted) => {
      const previous = sorted[order - 1];
      if (!previous) return;
      if (Math.abs(current.y - previous.y) < MIN_LABEL_GAP) visible[current.index] = false;
    });

  return visible;
}

const timeFormatter = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

const timeWithSecondsFormatter = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** Marca do eixo do tempo, com a precisão que a janela exige. */
function formatTimeTick(isoTime: string | undefined, labels: string[]): string {
  if (!isoTime) return '';

  const first = Date.parse(labels[0] ?? isoTime);
  const last = Date.parse(labels.at(-1) ?? isoTime);
  const spanMinutes = (last - first) / 60_000;
  const date = new Date(isoTime);

  if (spanMinutes <= 10) return timeWithSecondsFormatter.format(date);
  // Acima de 6 horas o dia entra na marca: numa janela de 24h os dois extremos
  // cairiam no mesmo horário, e "13:00 … 13:00" não diz qual é qual.
  if (spanMinutes <= 6 * 60) return timeFormatter.format(date);
  return dateTimeFormatter.format(date);
}

/** Instante completo, usado no tooltip e na tabela. */
function formatFullTime(isoTime: string | undefined): string {
  if (!isoTime) return '';
  return timeWithSecondsFormatter.format(new Date(isoTime));
}
